import { API_BASE_URL, TENANT_ID } from '../utils/api';

export interface AdminNodeView {
  nodeId: string;
  role: 'PLATFORM_POOL' | 'TENANT_DEDICATED';
  status: 'ONLINE' | 'OFFLINE' | 'BUSY' | 'DRAINING';
  tenantId?: string | null;
  tenantName?: string | null;
  tenantSlug?: string | null;
  channelInstanceId?: string | null;
  publicIp?: string | null;
  country?: string | null;
  carrier?: string | null;
  networkType?: string | null;
  batteryLevel?: number | null;
  isCharging?: boolean;
  latencyMs: number;
  activeStreams: number;
  totalBytesTransferred: string;
  lastHeartbeatAt?: string | null;
  connectedAt?: string | null;
  isAliveInMemory: boolean;
}

export interface AdminPoolSummary {
  status: 'HEALTHY' | 'DEGRADED' | 'CRITICAL';
  totalCapacityStreams: number;
  activeStreams: number;
  availableNodesCount: number;
  totalNodesCount: number;
  utilizationPercent: number;
  nodes: AdminNodeView[];
}

export interface AdminChannelInstanceRouting {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  instanceName: string;
  status: string;
  channelType: string;
  routingMode: 'IRIDIA_NODE' | 'POOL_FAILOVER' | 'CUSTOM_PROXY' | 'DIRECT';
  primaryNodeId?: string | null;
  currentNodeId?: string | null;
  proxyHost?: string | null;
  isFailoverActive: boolean;
  failoverReason?: string | null;
  failedAt?: string | null;
  nodeStatus?: 'ONLINE' | 'OFFLINE' | 'UNKNOWN';
  nodeBattery?: number | null;
  lastActiveAt?: string | null;
}

export interface AdminNodesOverview {
  summary: {
    totalNodes: number;
    onlineNodes: number;
    offlineNodes: number;
    poolNodesCount: number;
    tenantNodesCount: number;
    totalActiveStreams: number;
    instancesTotal: number;
    instancesUsingProxies: number;
    instancesInFailover: number;
  };
  nodes: AdminNodeView[];
  pool: AdminPoolSummary;
  instances: AdminChannelInstanceRouting[];
}

export interface PairingTokenPayload {
  nodeId?: string;
  role: 'PLATFORM_POOL' | 'TENANT_DEDICATED';
  tenantId?: string;
  channelInstanceId?: string;
  country?: string;
  expiresInDays?: number;
}

export interface PairingTokenResponse {
  success: boolean;
  nodeId: string;
  token: string;
  qrData: string;
}

export const nodesService = {
  async getOverview(token?: string | null): Promise<AdminNodesOverview> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
    };

    const res = await fetch(`${API_BASE_URL}/admin/nodes/overview`, { headers });
    if (!res.ok) {
      throw new Error(`Error ${res.status}: Fallo al obtener telemetría de nodos`);
    }
    return res.json();
  },

  async generatePairingToken(payload: PairingTokenPayload, token?: string | null): Promise<PairingTokenResponse> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
    };

    const res = await fetch(`${API_BASE_URL}/admin/nodes/pairing-token`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Error ${res.status}: Fallo al generar token de emparejamiento`);
    }

    return res.json();
  },

  async disconnectNode(nodeId: string, token?: string | null): Promise<{ success: boolean; message: string }> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
    };

    const res = await fetch(`${API_BASE_URL}/admin/nodes/disconnect/${nodeId}`, {
      method: 'POST',
      headers,
    });

    if (!res.ok) {
      throw new Error(`Error ${res.status}: Fallo al desconectar nodo`);
    }

    return res.json();
  },

  async rePairNode(nodeId: string, token?: string | null): Promise<PairingTokenResponse> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
    };

    const res = await fetch(`${API_BASE_URL}/admin/nodes/${nodeId}/re-pair`, {
      method: 'POST',
      headers,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Error ${res.status}: Fallo al regenerar QR de vinculación`);
    }

    return res.json();
  },

  async deleteNode(nodeId: string, token?: string | null): Promise<{ success: boolean; message: string }> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
    };

    const res = await fetch(`${API_BASE_URL}/admin/nodes/${nodeId}`, {
      method: 'DELETE',
      headers,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Error ${res.status}: Fallo al eliminar nodo`);
    }

    return res.json();
  },

  async getChannelQr(
    channelInstanceId: string,
    token?: string | null
  ): Promise<{ qrUrl?: string; qrRaw?: string; status: string }> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
    };

    const res = await fetch(`${API_BASE_URL}/admin/nodes/channel/${channelInstanceId}/qr`, { headers });
    if (!res.ok) {
      throw new Error(`Error ${res.status}: Fallo al obtener QR del canal`);
    }
    return res.json();
  },

  async setChannelRouting(
    channelInstanceId: string,
    payload: {
      routingMode: 'IRIDIA_NODE' | 'DIRECT' | 'CUSTOM_PROXY';
      nodeId?: string | null;
      allowPoolFallback?: boolean;
      proxyUrl?: string | null;
      proxyUsername?: string | null;
      proxyPassword?: string | null;
    },
    token?: string | null
  ): Promise<{ success: boolean; instanceId: string; routingMode: string; nodeId?: string | null }> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
    };

    const res = await fetch(`${API_BASE_URL}/admin/nodes/channel/${channelInstanceId}/routing`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Error ${res.status}: Fallo al actualizar enrutamiento`);
    }

    return res.json();
  },

  async sendTestMessage(
    channelInstanceId: string,
    payload: { to: string; text: string },
    token?: string | null
  ): Promise<{ success: boolean; messageId: string; to: string; channelInstanceId: string }> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
    };

    const res = await fetch(`${API_BASE_URL}/admin/nodes/channel/${channelInstanceId}/test-message`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Error ${res.status}: Fallo al enviar mensaje de prueba`);
    }

    return res.json();
  },

  async reconnectChannel(
    channelInstanceId: string,
    token?: string | null
  ): Promise<{ success: boolean; status: string }> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
    };

    const res = await fetch(`${API_BASE_URL}/admin/nodes/channel/${channelInstanceId}/reconnect`, {
      method: 'POST',
      headers,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Error ${res.status}: Fallo al reconectar canal`);
    }

    return res.json();
  },
};
